import React from 'react';
import { ProductDetailsCard } from '../organisms/ProductDetailsCard';
import { RetargetingProductDetailsCard } from '../organisms/DynamicTabbedLayout/RetargetingProductDetailsCard';
import { EnrollmentCardContainerComp } from '../organisms/EnrollmentCardContainer/EnrollmentCardContainerComp';
import type { SlotContent, SlotTrackingContext } from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';

import {
  normalizeProductDetailsCardProps,
  normalizeEnrollmentCardProps,
  normalizeLayeredFeatureCardProps,
  normalizeMerchandisingCoBrandCardProps,
  normalizeRiottMediaCardProps,
} from './slotPropNormalizers';
import { isRecord } from './helper';
import { buildDcaFallbackList3Entry, buildNonCobrandDcaList3Entry } from './polarisTrackingUtils';
import { isDcaTargetPayloadFailed } from './dynamicContentUtils';
import {
  buildRetargetingCandidateKey,
  buildRetargetingCfFallbackSlot,
  isRetargetingPayload,
} from './retargeting/retargetingRuntime';

// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  MerchandisingLayeredFeature,
  MerchandisingCoBrandCard,
  RiottMediaCardNonEditable,
} from '@marriott/mi-merch-components';

const CARD_TYPE_MAP = {
  layeredfeaturecard: 'layeredfeaturecard',
  productdetailscard: 'productdetailscard',
  enrollmentcard: 'enrollmentcard',
  cobrandcard: 'cobrandcard',
  riottmediacard: 'riottmediacard',
} as const;

type CardTypeKey = (typeof CARD_TYPE_MAP)[keyof typeof CARD_TYPE_MAP];

function isCardTypeKey(s: string): s is keyof typeof CARD_TYPE_MAP {
  return s in CARD_TYPE_MAP;
}

function normalizeCardType(input: string): CardTypeKey | null {
  const normalized = input
    .trim()
    .toLowerCase()
    .replace(/model$/, '');
  return isCardTypeKey(normalized) ? CARD_TYPE_MAP[normalized] : null;
}

function getDcaCardType(metaData: Record<string, unknown> | undefined): CardTypeKey {
  if (!metaData || typeof metaData !== 'object') return CARD_TYPE_MAP.productdetailscard;
  if (metaData['isMango'] === true) return CARD_TYPE_MAP.riottmediacard;
  if ((metaData['type'] as string)?.toLowerCase() === 'cobrand') return CARD_TYPE_MAP.cobrandcard;
  if ((metaData['type'] as string)?.toLowerCase() === 'non-cobrand') return CARD_TYPE_MAP.productdetailscard;
  return CARD_TYPE_MAP.productdetailscard;
}

/** Is the payload a CF-shaped payload */
function isCfFragmentPayload(payload: unknown): boolean {
  if (payload == null || typeof payload !== 'object') return false;
  const cardType = (payload as Record<string, unknown>)['cardType'];
  return typeof cardType === 'string' && cardType.length > 0;
}

/**
 * Prefer live payload for card-type resolution. Only use `cfFallback`’s `cardType` when there is **no** live
 * Target object yet (`payload` null/undefined); otherwise UXL payloads without `cardType` would incorrectly
 * follow CF’s card type instead of `metaData`/`metadata` (Riott, CoBrand, etc.).
 */
function getPayloadForCfCardTypeResolution(slot: SlotContent, rawPayload: unknown): unknown {
  if (isCfFragmentPayload(rawPayload)) return rawPayload;
  if (rawPayload != null) return rawPayload;
  if (
    (slot?.type === 'dca' || slot?.type === 'geo-cf') &&
    isRecord(slot?.cfFallback) &&
    isCfFragmentPayload(slot.cfFallback)
  ) {
    return slot.cfFallback;
  }
  return rawPayload;
}

/**
 * DCA slots with null/invalid Target payloads still resolve to the correct card so merch/PDC loading UI runs.
 * Merges mbox from dcaTarget; does not copy CF fallback content into the normalized props.
 */
function buildDcaPayloadForNormalizers(slot: SlotContent): unknown {
  if (slot.type !== 'dca') return slot.payload;
  const mbox = slot.dcaTarget?.mboxParameter;
  const p = slot.payload;

  if (p === null || p === undefined) {
    return {
      mboxName: mbox,
      mboxParameter: mbox,
      isTargetContentLoaded: false,
    };
  }
  if (!isRecord(p)) return p;
  if (isDcaTargetPayloadFailed(p)) {
    return {
      ...p,
      mboxName: (p['mboxName'] as string | undefined) ?? mbox,
      mboxParameter: (p['mboxParameter'] as string | undefined) ?? mbox,
    };
  }
  return p;
}

function resolveEffectiveSlot(slot: SlotContent): { effectiveSlot: SlotContent; resolvedCardType: CardTypeKey } {
  const size = (slot?.size as 'square' | 'tall' | 'banner') ?? 'square';
  const rawPayload = slot?.payload;
  const effectivePayload = slot?.type === 'dca' ? buildDcaPayloadForNormalizers(slot) : rawPayload;
  const effectiveSlot: SlotContent = effectivePayload === slot.payload ? slot : { ...slot, payload: effectivePayload };
  const payloadForCfResolution = getPayloadForCfCardTypeResolution(slot, rawPayload);
  // geo-cf slots resolve exactly like cf slots (CF-shaped payloads → cardType).
  const useCfCardTypeResolution =
    slot?.type === 'cf' ||
    slot?.type === 'geo-cf' ||
    (slot?.type === 'dca' && isCfFragmentPayload(payloadForCfResolution));
  const resolvedCardType =
    size === 'banner'
      ? CARD_TYPE_MAP.layeredfeaturecard
      : useCfCardTypeResolution
      ? normalizeCardType(String((payloadForCfResolution as Record<string, unknown>)?.['cardType'] ?? '')) ||
        CARD_TYPE_MAP.productdetailscard
      : getDcaCardType(
          ((effectivePayload as Record<string, unknown>)?.['metaData'] ??
            (effectivePayload as Record<string, unknown>)?.['metadata']) as Record<string, unknown> | undefined
        );

  return { effectiveSlot, resolvedCardType };
}

export function getDcaNonCobrandImpressionEntry(slot: SlotContent, trackingCtx?: SlotTrackingContext): string | null {
  if (slot?.type === 'cf') return buildDcaFallbackList3Entry(slot);
  if (slot?.type !== 'dca') return null;
  const { effectiveSlot, resolvedCardType } = resolveEffectiveSlot(slot);
  const ctx = { slot: effectiveSlot, trackingCtx };

  if (resolvedCardType === CARD_TYPE_MAP.layeredfeaturecard) {
    const props = normalizeLayeredFeatureCardProps(ctx);
    return buildNonCobrandDcaList3Entry(props.trackingProperties as Record<string, unknown> | undefined);
  }

  if (resolvedCardType === CARD_TYPE_MAP.productdetailscard) {
    const props = normalizeProductDetailsCardProps(ctx);
    return buildNonCobrandDcaList3Entry(props.trackingProperties as Record<string, unknown> | undefined);
  }

  return null;
}

export function resolveSlotComponent(slot: SlotContent, trackingCtx?: SlotTrackingContext): React.ReactElement | null {
  if (slot?.type === 'retargeting') {
    if (isRetargetingPayload(slot?.payload)) {
      const fallbackSlot = buildRetargetingCfFallbackSlot(slot);
      const fallback = fallbackSlot ? resolveSlotComponent(fallbackSlot, trackingCtx) : null;
      const candidateKey = buildRetargetingCandidateKey(slot.payload);

      return <RetargetingProductDetailsCard key={candidateKey} cardProps={slot.payload} fallback={fallback} />;
    }

    const fallbackSlot = buildRetargetingCfFallbackSlot(slot);
    return fallbackSlot ? resolveSlotComponent(fallbackSlot, trackingCtx) : null;
  }

  const { effectiveSlot, resolvedCardType } = resolveEffectiveSlot(slot);
  const ctx = { slot: effectiveSlot, trackingCtx };

  switch (resolvedCardType) {
    case CARD_TYPE_MAP.layeredfeaturecard:
      return <MerchandisingLayeredFeature {...normalizeLayeredFeatureCardProps(ctx)} />;
    case CARD_TYPE_MAP.enrollmentcard:
      return <EnrollmentCardContainerComp {...normalizeEnrollmentCardProps(ctx)} />;
    case CARD_TYPE_MAP.cobrandcard:
      return <MerchandisingCoBrandCard {...(normalizeMerchandisingCoBrandCardProps(ctx) as object)} />;
    case CARD_TYPE_MAP.riottmediacard: {
      return <RiottMediaCardNonEditable {...(normalizeRiottMediaCardProps(ctx) as object)} />;
    }
    case CARD_TYPE_MAP.productdetailscard:
    default:
      return <ProductDetailsCard {...normalizeProductDetailsCardProps(ctx)} />;
  }
}
